import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function MethodologyWidget({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const defaultProfileImage = '/img/methodology-profile.webp';
    const deleteLogo = '/img/delete.svg';

    const fonts = useFont();
    
    // INLINE-EDITING STATES
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    const [isUploadingProfile, setIsUploadingProfile] = useState(false);
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    const widgetRef = useRef(null);

    // Animation Trigger Effect (2025 Animation System)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    // Layout Configuration
    const getLayoutConfig = () => {
        const layoutType = data.layoutType || 'classic-methodology';
        
        const layouts = {
            'classic-methodology': {
                topDirection: 'row',
                topImagePosition: 'right',
                topTextAlign: 'left',
                stepsDirection: 'row',
                stepsAlignment: 'left',
                imageSize: 'normal',
                containerPadding: true,
                mobileStack: true,
                description: 'Text left, image right, horizontal steps'
            },
            'image-top-centered': {
                topDirection: 'column',
                topImagePosition: 'top',
                topTextAlign: 'center',
                stepsDirection: 'row',
                stepsAlignment: 'center',
                imageSize: 'large',
                containerPadding: true,
                mobileStack: false,
                description: 'Image on top, centered text, centered steps'
            },
            'steps-vertical': {
                topDirection: 'row',
                topImagePosition: 'right',
                topTextAlign: 'left',
                stepsDirection: 'column',
                stepsAlignment: 'left',
                imageSize: 'normal',
                containerPadding: true,
                mobileStack: true,
                description: 'Classic top layout, vertical step layout'
            },
            'minimal-text-focus': {
                topDirection: 'column',
                topImagePosition: 'hidden',
                topTextAlign: 'center',
                stepsDirection: 'row',
                stepsAlignment: 'center',
                imageSize: 'hidden',
                containerPadding: true,
                mobileStack: false,
                description: 'Text-only focused layout, no images'
            }
        };
        
        return layouts[layoutType] || layouts['classic-methodology'];
    };

    const layoutConfig = getLayoutConfig();

    // Underlay Position Calculator
    const getUnderlayPositionStyles = () => {
        const position = data.underlayPosition || 'bottom-left';
        const baseSize = data.underlaySize || 200;
        const size = isMobileLayout ? Math.min(baseSize, 150) : baseSize; // Smaller on mobile
        const offset = isMobileLayout ? 10 : 20; // Closer to image on mobile
        
        const positions = {
            'bottom-left': {
                bottom: `-${offset}px`,
                left: `-${offset}px`,
                transform: `rotate(${data.underlayRotation || 0}deg)`
            },
            'bottom-right': {
                bottom: `-${offset}px`,
                right: `-${offset}px`,
                transform: `rotate(${data.underlayRotation || 0}deg)`
            },
            'top-left': {
                top: `-${offset}px`,
                left: `-${offset}px`,
                transform: `rotate(${data.underlayRotation || 0}deg)`
            },
            'top-right': {
                top: `-${offset}px`,
                right: `-${offset}px`,
                transform: `rotate(${data.underlayRotation || 0}deg)`
            }
        };
        
        return {
            position: 'absolute',
            width: `${size}px`,
            height: `${size}px`,
            opacity: data.underlayOpacity || 0.3,
            zIndex: -1,
            pointerEvents: 'none',
            filter: data.underlayInvert ? 'invert(1)' : 'none',
            ...positions[position]
        };
    };

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);

    // 🚀 REVOLUTIONARY LAYOUT SYSTEM - Responsive Widget Styles
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none'
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 800)
        })
    };

    // INLINE-EDITING CORE FUNCTIONS
    const handleInlineEdit = (fieldName, currentValue) => {
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        if (isInlineEditing === fieldName) {
            handleInlineCancel(); // Toggle: Beenden wenn bereits aktiv
            return;
        }
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = useCallback((fieldName) => {
        if (!onUpdate) {
            console.warn('⚠️ onUpdate not available');
            return;
        }
        
        console.log('💾 MethodologyWidget Saving field:', fieldName, 'with value:', inlineValue);
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        setIsInlineEditing(null);
        setInlineValue('');
        setOriginalLayout({});
        console.log('✅ MethodologyWidget Save completed for:', fieldName);
    }, [onUpdate, data, inlineValue]);

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
        setOriginalLayout({});
    };

    const handleInlineKeyPress = (e, fieldName) => {
        const isTextarea = fieldName.includes('description') || fieldName.includes('additional') || 
                          fieldName.includes('conclusion') || fieldName.includes('mainDescription');
        
        if (e.key === 'Enter') {
            if (isTextarea) {
                if (e.ctrlKey || e.metaKey) {
                    handleInlineSubmit(fieldName);
                }
                // Normale Enter für neue Zeile
            } else {
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    const handleElementClick = (e, fieldName, currentValue) => {
        e.preventDefault();
        e.stopPropagation();
        
        const rect = e.target.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(e.target);
        
        // LEAN Layout-Erfassung - nur essenzielle Eigenschaften
        const layoutData = {
            width: rect.width + 'px',
            height: rect.height + 'px',
            marginTop: computedStyle.marginTop,
            marginBottom: computedStyle.marginBottom,
            marginLeft: computedStyle.marginLeft,
            marginRight: computedStyle.marginRight
        };
        
        setOriginalLayout(layoutData);
        handleInlineEdit(fieldName, currentValue);
    };

    const handleImageUpload = async (fieldName, setIsUploading) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file && onUpdate) {
                setIsUploading(true);
                try {
                    // Hier würde normalerweise der Upload-Service aufgerufen werden
                    // Für Demo-Zwecke verwenden wir eine lokale URL
                    const imageUrl = URL.createObjectURL(file);
                    const updatedData = { ...data, [fieldName]: imageUrl };
                    onUpdate(updatedData);
                } catch (error) {
                    console.error('Upload failed:', error);
                } finally {
                    setIsUploading(false);
                }
            }
        };
        input.click();
    };

    // SMART OUTSIDE-CLICK HANDLER (EXACT COPY FROM SIMPLISTIC QUOTE)
    useEffect(() => {
        if (!isInlineEditing) return;

        const handleOutsideClick = (event) => {
            // Debug-Logging für Troubleshooting
            console.log('🔍 MethodologyWidget Outside Click Debug:', {
                target: event.target.tagName,
                isInlineEditing,
                inlineValue: inlineValue.substring(0, 20) + '...'
            });

            const isEditingInput = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA';
            const isActionButton = event.target.hasAttribute('data-action-button') || event.target.closest('[data-action-button]');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit-container');
            const isWithinWidget = widgetRef.current?.contains(event.target);
            
            // ✅ NEUE LOGIK: Klick auf anderes Text-Element = Outside-Click
            const isClickOnOtherTextElement = event.target.tagName === 'P' || 
                                            event.target.tagName === 'H1' || 
                                            event.target.tagName === 'H2' || 
                                            event.target.tagName === 'H3' || 
                                            event.target.tagName === 'DIV';
            
            // Outside-Click = Echter Outside-Click ODER Klick auf anderes Text-Element
            const isRealOutsideClick = (!isEditingInput && !isActionButton && !isDeleteButton && !isWidgetEdit && !isWithinWidget) ||
                                      (isClickOnOtherTextElement && isWithinWidget && !isEditingInput);

            console.log('🔍 MethodologyWidget Outside Click Checks:', {
                isEditingInput,
                isActionButton,
                isDeleteButton,
                isWidgetEdit,
                isWithinWidget,
                isClickOnOtherTextElement,
                isRealOutsideClick
            });

            if (isRealOutsideClick && isInlineEditing) {
                console.log('✅ MethodologyWidget Saving via outside click:', isInlineEditing, inlineValue);
                handleInlineSubmit(isInlineEditing);
            }
        };

        document.addEventListener('click', handleOutsideClick, true);
        return () => document.removeEventListener('click', handleOutsideClick, true);
    }, [isInlineEditing, inlineValue, handleInlineSubmit]);

    // RENDER EDITABLE TEXT FUNCTION (UNIVERSELLE TEXTAREA-LÖSUNG)
    const renderEditableText = (fieldName, currentValue, Element, style, placeholder) => {
        const isEditing = isInlineEditing === fieldName;
        const layoutData = originalLayout;

        if (isEditing && isEditMode) {
            return (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        className="inline-editing-textarea"
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            // ✅ Cursor-Position merken BEVOR wir die Höhe ändern
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                // ✅ Cursor-Position wiederherstellen
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        placeholder={placeholder}
                        ref={(element) => {
                            if (element) {
                                setTimeout(() => {
                                    // ✅ NUR beim ersten Laden ans Ende setzen, nicht bei jedem Re-Render
                                    if (element.selectionStart === 0 && element.selectionEnd === 0) {
                                        element.setSelectionRange(element.value.length, element.value.length);
                                    }
                                    element.style.height = 'auto';
                                    element.style.height = element.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '0',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            whiteSpace: 'pre-wrap',
                            color: style.color || '#333333', // ✅ Explizite Farb-Übertragung
                            fontSize: style.fontSize || 'inherit',
                            fontWeight: style.fontWeight || 'inherit',
                            fontFamily: style.fontFamily || 'inherit',
                            lineHeight: style.lineHeight || 'inherit'
                        }}
                    />
                    {/* Action Buttons */}
                    <div style={{
                        position: 'absolute',
                        top: '-11px',
                        right: '-6px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(4px)',
                        borderRadius: '6px',
                        padding: '2px 4px',
                        display: 'flex',
                        gap: '4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        zIndex: 9999
                    }}>
                        <button 
                            data-action-button="save"
                            onClick={() => handleInlineSubmit(fieldName)}
                            style={{
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                padding: '0',
                                border: 'none',
                                background: '#10b981',
                                color: 'white',
                                fontSize: '10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >✓</button>
                        <button 
                            data-action-button="cancel"
                            onClick={handleInlineCancel}
                            style={{
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                padding: '0',
                                border: 'none',
                                background: '#ef4444',
                                color: 'white',
                                fontSize: '10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >×</button>
                    </div>
                </div>
            );
        }

        // Normal display with hover effects
        return React.createElement(Element, {
            style: {
                ...style,
                cursor: isEditMode ? 'text' : 'default',
                transition: 'all 0.3s ease',
                whiteSpace: 'pre-wrap'  // ✅ KRITISCH: Whitespace für Anzeige beibehalten
            },
            onClick: isEditMode ? (e) => handleElementClick(e, fieldName, currentValue) : undefined,
            onMouseEnter: isEditMode ? (e) => {
                e.target.style.background = 'linear-gradient(45deg, rgba(255, 255, 255, 0.02) 0%, rgba(192, 192, 192, 0.04) 25%, rgba(255, 255, 255, 0.06) 50%, rgba(192, 192, 192, 0.04) 75%, rgba(255, 255, 255, 0.02) 100%)';
                e.target.style.animation = 'silverGlow 3s ease-in-out infinite';
                e.target.style.backdropFilter = 'blur(8px)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            } : undefined,
            onMouseLeave: isEditMode ? (e) => {
                e.target.style.background = 'transparent';
                e.target.style.animation = 'none';
                e.target.style.backdropFilter = 'none';
                e.target.style.boxShadow = 'none';
            } : undefined
        }, currentValue || placeholder);
    };

    // 🚀 REVOLUTIONARY LAYOUT SYSTEM - Mobile Breakpoint Detection (750px)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // ✅ Standardized Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    return (
        <>
            <style>
                {`
                    .inline-editing-textarea {
                        caret-color: currentColor;
                    }
                    
                    @keyframes silverGlow {
                        0% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                        50% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.04) 0%,
                                rgba(192, 192, 192, 0.05) 25%,
                                rgba(255, 255, 255, 0.06) 50%,
                                rgba(192, 192, 192, 0.05) 75%,
                                rgba(255, 255, 255, 0.04) 100%
                            );
                        }
                        100% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                    }
                    @keyframes methodologyImageUploadSpin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    /* Methodology Animation Keyframes (2025 Animation System) */
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateX(-30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    
                    @keyframes fadeInSlow {
                        0% { opacity: 0; }
                        30% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                    
                    /* Methodology Animation Classes */
                    .methodology-animated {
                        animation-fill-mode: both;
                    }
                    
                    .methodology-animated.fadeInUp {
                        animation-name: fadeInUp;
                    }
                    
                    .methodology-animated.slideIn {
                        animation-name: slideIn;
                    }
                    
                    .methodology-animated.fadeIn {
                        animation-name: fadeIn;
                    }
                    
                    .methodology-animated.fadeInSlow {
                        animation-name: fadeInSlow;
                    }
                `}
            </style>
            <div 
                className="widget-container methodology-widget" 
                style={widgetStyle} 
                ref={(el) => { containerRef.current = el; widgetRef.current = el; }}
            >
            {/* Delete Button */}
            {isEditMode && (
                <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}
            
            {/* 🤖 AI REFRESH ICON */}
            {isEditMode && (
                <AIRefreshIcon 
                    widget={{
                        ...data,
                        type: 'MethodologyWidget'
                    }}
                    spaceId={null}
                    onContentRefreshed={(refreshedWidget) => {
                        console.log('🤖 [METHODOLOGYWIDGET] AI-refreshed content received:', refreshedWidget);
                        onUpdate(refreshedWidget);
                    }}
                    position="top-left"
                />
            )}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={(event) => handleDelete(event, data.id)}
                />
            )}
            
            {/* Main Container - 🚀 Revolutionary Layout System */}
            <div style={{
                padding: `${getResponsiveValue(data, 'paddingTop', isMobileLayout, isMobileLayout ? 30 : 60)}px ${isMobileLayout ? 16 : 40}px ${getResponsiveValue(data, 'paddingBottom', isMobileLayout, isMobileLayout ? 30 : 60)}px ${isMobileLayout ? 16 : 40}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: appendPx(isMobileLayout ? Math.min(data.contentSpacing || 60, 40) : data.contentSpacing || 60),
                minHeight: '100%'
            }}>
                
                {/* Top Section: Dynamic Layout */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobileLayout && layoutConfig.mobileStack ? 'column' : 
                                   layoutConfig.topDirection === 'column' ? 'column' : 'row',
                    gap: isMobileLayout ? '24px' : '40px',
                    alignItems: layoutConfig.topDirection === 'column' ? 'center' : 
                              isMobileLayout ? 'center' : 'flex-start',
                    justifyContent: layoutConfig.topTextAlign === 'center' ? 'center' : 'flex-start'
                }}>
                    
                    {/* Content Section */}
                    <div style={{
                        flex: '1',
                        textAlign: isMobileLayout ? 'center' : layoutConfig.topTextAlign,
                        order: layoutConfig.topDirection === 'column' ? 2 : 
                              (isMobileLayout && layoutConfig.mobileStack) ? 2 : 1,
                        width: layoutConfig.topDirection === 'column' ? '100%' : 'auto',
                        maxWidth: '600px',
                        margin: isMobileLayout && layoutConfig.topTextAlign === 'center' ? '0 auto' : '0'
                    }}>
                        {/* Main Title with Animation */}
                        {data.mainTitle && (
                            <div 
                                key={`title-${animationKey}`}
                                className={data.enableAnimations && isEditMode ? `methodology-animated ${data.animationType || 'fadeInUp'}` : ''}
                                style={{
                                    animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                                    animationDelay: data.enableAnimations && isEditMode ? '0ms' : '0s'
                                }}
                            >
                                {data.showMainTitle !== false && renderEditableText(
                                    'mainTitle',
                                    data.mainTitle,
                                    'h1',
                                    {
                                ...((!data.mainTitleEffect || data.mainTitleEffect === 'none') && {
                                    color: data.mainTitleColor || data.titleColor || '#000000' // ✅ IMPROVED: Besserer Fallback
                                }),
                                        fontSize: appendPx(data.mainTitleSize || 48),
                                        fontWeight: data.mainTitleWeight || '400',
                                        fontFamily: data.mainTitleFont || fonts.globalBigFont,
                                        fontStyle: data.mainTitleStyle || 'italic',
                                        marginBottom: '20px',
                                        lineHeight: '1.2',
                                        letterSpacing: '2px',
                                        ...getTextEffectStyles(data.mainTitleEffect),
                                        // Responsive Font-Sizes
                                        '--mobile-size': appendPx(data.mainTitleSizeMobile || data.mainTitleSize || 48),
                                        '--normal-size': appendPx(data.mainTitleSize || 48),
                                        '--big-size': appendPx(data.mainTitleSizeBig || data.mainTitleSize || 48)
                                    },
                                    'Enter your main title...'
                                )}
                            </div>
                        )}

                        {/* Main Description */}
                        {data.showMainDescription !== false && data.mainDescription && renderEditableText(
                            'mainDescription',
                            data.mainDescription,
                            'p',
                            {
                                ...((!data.mainDescriptionEffect || data.mainDescriptionEffect === 'none') && {
                                    color: data.mainDescriptionColor || data.stepDescriptionColor || data.additionalTextColor || '#666666' // ✅ IMPROVED: Mehrere Space-DB Fallbacks
                                }),
                                fontSize: appendPx(data.mainDescriptionSize || 18),
                                fontWeight: data.mainDescriptionWeight || '400',
                                fontFamily: data.mainDescriptionFont || fonts.globalSmallFont,
                                fontStyle: data.mainDescriptionStyle || 'italic',
                                marginBottom: '20px',
                                lineHeight: '1.6',
                                ...getTextEffectStyles(data.mainDescriptionEffect),
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.mainDescriptionSizeMobile || data.mainDescriptionSize || 18),
                                '--normal-size': appendPx(data.mainDescriptionSize || 18),
                                '--big-size': appendPx(data.mainDescriptionSizeBig || data.mainDescriptionSize || 18)
                            },
                            'Enter your main description...'
                        )}

                        {/* 📝 RICH TEXT MAIN CONTENT - Ersetzt alle Additional Texts */}
                        {data.showMainContent !== false && (
                            <div 
                                style={{
                                    color: data.additionalTextColor || data.mainDescriptionColor || '#333333',
                                    fontSize: appendPx(data.additionalTextSize || 16),
                                    fontWeight: data.additionalTextWeight || '400',
                                    fontFamily: data.additionalTextFont || fonts.globalSmallFont,
                                    lineHeight: '1.6',
                                    marginBottom: '20px'
                                }}
                                dangerouslySetInnerHTML={{ 
                                    __html: data.mainContent || `
                                        <p>What I teach is the alchemy of feminine leadership that gets you results in <strong>business, love, and life.</strong></p>
                                        <p>A synthesis of transformational coaching that weaves together ancient wisdom with modern neuroscience.</p>
                                        <p><em>It initiates her into her truest, biggest, boldest self.</em></p>
                                    `
                                }}
                            />
                        )}
                    </div>

                    {/* Image Section - Conditional Rendering */}
                    {layoutConfig.topImagePosition !== 'hidden' && data.profileImage && (
                        <div style={{
                            flex: layoutConfig.topDirection === 'column' ? 'none' : 
                                  isMobileLayout ? 'none' : '0 0 auto',
                            order: layoutConfig.topDirection === 'column' ? 1 : 
                                  (layoutConfig.topImagePosition === 'left' ? 1 : 2),
                            marginBottom: (isMobileLayout && layoutConfig.mobileStack) || 
                                         layoutConfig.topDirection === 'column' ? 
                                         (isMobileLayout ? '16px' : '20px') : '0',
                            display: 'flex',
                            justifyContent: 'center',
                            width: layoutConfig.topDirection === 'column' ? '100%' : 'auto',
                            position: 'relative' // For underlay positioning
                        }}>
                            {/* Main Profile Image with Upload */}
                            <div 
                                style={{ position: 'relative', display: 'inline-block' }}
                                onMouseEnter={(e) => {
                                    const uploadBtn = e.currentTarget.querySelector('.upload-btn');
                                    if (uploadBtn && isEditMode) uploadBtn.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                    const uploadBtn = e.currentTarget.querySelector('.upload-btn');
                                    if (uploadBtn) uploadBtn.style.opacity = '0';
                                }}
                            >
                                <img 
                                    src={data.profileImage || defaultProfileImage}
                                    alt="Profile"
                                    style={{
                                        width: appendPx(
                                            layoutConfig.imageSize === 'large' ? 
                                            (isMobileLayout ? Math.min(data.profileImageWidth || 500, 280) : data.profileImageWidth || 500) :
                                            isMobileLayout ? Math.min(data.profileImageWidth || 400, 250) : data.profileImageWidth || 400
                                        ),
                                        height: appendPx(
                                            layoutConfig.imageSize === 'large' ?
                                            (isMobileLayout ? 'auto' : data.profileImageHeight || 600) :
                                            isMobileLayout ? 'auto' : data.profileImageHeight || 500
                                        ),
                                        objectFit: 'cover',
                                        borderRadius: isMobileLayout ? '6px' : '8px',
                                        maxWidth: '100%'
                                    }}
                                />
                                
                                {/* Upload Button */}
                                {isEditMode && (
                                    <button 
                                        className="optional-upload-btn"
                                        onClick={() => handleImageUpload('profileImage', setIsUploadingProfile)}
                                        style={{
                                            position: 'absolute',
                                            top: '10px',
                                            left: '10px',
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: '#6366f1',
                                            border: 'none',
                                            opacity: 0,
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 100
                                        }}
                                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                    >
                                        {isUploadingProfile ? (
                                            <div 
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    border: '2px solid white',
                                                    borderTop: '2px solid transparent',
                                                    borderRadius: '50%',
                                                    animation: 'methodologyImageUploadSpin 1s linear infinite'
                                                }} 
                                            />
                                        ) : (
                                            <img 
                                                src="/img/upload.svg" 
                                                alt="Upload" 
                                                style={{
                                                    width: '18px',
                                                    height: '18px',
                                                    filter: 'invert(1)'
                                                }}
                                            />
                                        )}
                                    </button>
                                )}
                            </div>
                            
                            {/* Underlay Image */}
                            {data.showUnderlay && data.underlayImage && (
                                <img
                                    src={data.underlayImage}
                                    alt="Underlay decoration"
                                    style={getUnderlayPositionStyles()}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* Bottom Section: Steps - Dynamic Layout */}
                <div style={{
                    display: 'flex',
                    flexDirection: layoutConfig.stepsDirection === 'column' ? 'column' :
                                   isMobileLayout ? 'column' : 'row',
                    gap: appendPx(isMobileLayout ? Math.min(data.stepSpacing || 40, 24) : data.stepSpacing || 40),
                    marginTop: isMobileLayout ? '24px' : '40px',
                    alignItems: isMobileLayout ? 'center' : 
                               layoutConfig.stepsAlignment === 'center' ? 'center' : 'flex-start',
                    justifyContent: isMobileLayout ? 'center' :
                                   layoutConfig.stepsAlignment === 'center' ? 'center' : 'flex-start',
                    maxWidth: layoutConfig.stepsDirection === 'column' ? '600px' : '100%',
                    margin: layoutConfig.stepsDirection === 'column' && layoutConfig.stepsAlignment === 'center' ? 
                           (isMobileLayout ? '24px auto 0 auto' : '40px auto 0 auto') : 
                           (isMobileLayout ? '24px auto 0 auto' : '40px 0 0 0')
                }}>
                    
                    {/* Step 1 with Animation */}
                    <div 
                        key={`step1-${animationKey}`}
                        className={data.enableAnimations && isEditMode ? `methodology-animated ${data.animationType || 'fadeInUp'}` : ''}
                        style={{
                            flex: layoutConfig.stepsDirection === 'column' ? 'none' : '1',
                            textAlign: layoutConfig.stepsAlignment === 'center' || isMobileLayout ? 'center' : 'left',
                            width: layoutConfig.stepsDirection === 'column' ? '100%' : 'auto',
                            marginBottom: layoutConfig.stepsDirection === 'column' || isMobileLayout ? 
                                         (isMobileLayout ? '20px' : '30px') : '0',
                            maxWidth: isMobileLayout ? '300px' : '600px',
                            margin: isMobileLayout ? '0 auto 20px auto' : 
                                   layoutConfig.stepsDirection === 'column' ? '0 0 30px 0' : '0',
                            animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                            animationDelay: data.enableAnimations && isEditMode ? `${data.animationDelay || 200}ms` : '0s'
                        }}
                    >
                        {data.step1Number && renderEditableText(
                            'step1Number',
                            data.step1Number,
                            'div',
                            {
                                ...((!data.stepNumberEffect || data.stepNumberEffect === 'none') && {
                                    color: data.stepNumberColor || data.mainTitleColor || data.titleColor || '#000000' // ✅ IMPROVED: Mehrere Fallbacks
                                }),
                                fontSize: appendPx(data.stepNumberSize || 72),
                                fontWeight: data.stepNumberWeight || '300',
                                fontFamily: data.stepNumberFont || fonts.globalBigFont,
                                fontStyle: data.stepNumberStyle || 'italic',
                                marginBottom: '16px',
                                lineHeight: '1',
                                ...getTextEffectStyles(data.stepNumberEffect),
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepNumberSizeMobile || data.stepNumberSize || 72),
                                '--normal-size': appendPx(data.stepNumberSize || 72),
                                '--big-size': appendPx(data.stepNumberSizeBig || data.stepNumberSize || 72)
                            },
                            'Step number...'
                        )}
                        
                        {data.step1Title && renderEditableText(
                            'step1Title',
                            data.step1Title,
                            'h3',
                            {
                                ...((!data.stepTitleEffect || data.stepTitleEffect === 'none') && {
                                    color: data.stepTitleColor || data.mainTitleColor || data.titleColor || '#000000' // ✅ IMPROVED: Mehrere Fallbacks
                                }),
                                fontSize: appendPx(data.stepTitleSize || 24),
                                fontWeight: data.stepTitleWeight || '400',
                                fontFamily: data.stepTitleFont || fonts.globalBigFont,
                                fontStyle: data.stepTitleStyle || 'italic',
                                marginBottom: '12px',
                                lineHeight: '1.3',
                                ...getTextEffectStyles(data.stepTitleEffect),
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepTitleSizeMobile || data.stepTitleSize || 24),
                                '--normal-size': appendPx(data.stepTitleSize || 24),
                                '--big-size': appendPx(data.stepTitleSizeBig || data.stepTitleSize || 24)
                            },
                            'Step 1 title...'
                        )}
                        
                        {data.step1Description && renderEditableText(
                            'step1Description',
                            data.step1Description,
                            'p',
                            {
                                color: data.stepDescriptionColor || data.mainDescriptionColor || data.additionalTextColor || '#666666', // ✅ IMPROVED: Mehrere Space-DB Fallbacks
                                fontSize: appendPx(data.stepDescriptionSize || 14),
                                fontWeight: data.stepDescriptionWeight || '300',
                                fontFamily: data.stepDescriptionFont || fonts.globalSmallFont,
                                lineHeight: '1.5',
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepDescriptionSizeMobile || data.stepDescriptionSize || 14),
                                '--normal-size': appendPx(data.stepDescriptionSize || 14),
                                '--big-size': appendPx(data.stepDescriptionSizeBig || data.stepDescriptionSize || 14)
                            },
                            'Step 1 description...'
                        )}
                    </div>

                    {/* Step 2 with Animation */}
                    <div 
                        key={`step2-${animationKey}`}
                        className={data.enableAnimations && isEditMode ? `methodology-animated ${data.animationType || 'fadeInUp'}` : ''}
                        style={{
                            flex: layoutConfig.stepsDirection === 'column' ? 'none' : '1',
                            textAlign: layoutConfig.stepsAlignment === 'center' || isMobileLayout ? 'center' : 'left',
                            width: layoutConfig.stepsDirection === 'column' ? '100%' : 'auto',
                            marginBottom: layoutConfig.stepsDirection === 'column' || isMobileLayout ? 
                                         (isMobileLayout ? '20px' : '30px') : '0',
                            maxWidth: isMobileLayout ? '300px' : '600px',
                            margin: isMobileLayout ? '0 auto 20px auto' : 
                                   layoutConfig.stepsDirection === 'column' ? '0 0 30px 0' : '0',
                            animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                            animationDelay: data.enableAnimations && isEditMode ? `${(data.animationDelay || 200) * 2}ms` : '0s'
                        }}
                    >
                        {data.step2Number && renderEditableText(
                            'step2Number',
                            data.step2Number,
                            'div',
                            {
                                ...((!data.stepNumberEffect || data.stepNumberEffect === 'none') && {
                                    color: data.stepNumberColor || data.mainTitleColor || data.titleColor || '#000000' // ✅ IMPROVED: Mehrere Fallbacks
                                }),
                                fontSize: appendPx(data.stepNumberSize || 72),
                                fontWeight: data.stepNumberWeight || '300',
                                fontFamily: data.stepNumberFont || fonts.globalBigFont,
                                fontStyle: data.stepNumberStyle || 'italic',
                                marginBottom: '16px',
                                lineHeight: '1',
                                ...getTextEffectStyles(data.stepNumberEffect),
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepNumberSizeMobile || data.stepNumberSize || 72),
                                '--normal-size': appendPx(data.stepNumberSize || 72),
                                '--big-size': appendPx(data.stepNumberSizeBig || data.stepNumberSize || 72)
                            },
                            'Step 2 number...'
                        )}
                        
                        {data.step2Title && renderEditableText(
                            'step2Title',
                            data.step2Title,
                            'h3',
                            {
                                ...((!data.stepTitleEffect || data.stepTitleEffect === 'none') && {
                                    color: data.stepTitleColor || data.mainTitleColor || data.titleColor || '#000000' // ✅ IMPROVED: Mehrere Fallbacks
                                }),
                                fontSize: appendPx(data.stepTitleSize || 24),
                                fontWeight: data.stepTitleWeight || '400',
                                fontFamily: data.stepTitleFont || fonts.globalBigFont,
                                fontStyle: data.stepTitleStyle || 'italic',
                                marginBottom: '12px',
                                lineHeight: '1.3',
                                ...getTextEffectStyles(data.stepTitleEffect),
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepTitleSizeMobile || data.stepTitleSize || 24),
                                '--normal-size': appendPx(data.stepTitleSize || 24),
                                '--big-size': appendPx(data.stepTitleSizeBig || data.stepTitleSize || 24)
                            },
                            'Step 2 title...'
                        )}
                        
                        {data.step2Description && renderEditableText(
                            'step2Description',
                            data.step2Description,
                            'p',
                            {
                                color: data.stepDescriptionColor || data.mainDescriptionColor || data.additionalTextColor || '#666666', // ✅ IMPROVED: Mehrere Space-DB Fallbacks
                                fontSize: appendPx(data.stepDescriptionSize || 14),
                                fontWeight: data.stepDescriptionWeight || '300',
                                fontFamily: data.stepDescriptionFont || fonts.globalSmallFont,
                                lineHeight: '1.5',
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepDescriptionSizeMobile || data.stepDescriptionSize || 14),
                                '--normal-size': appendPx(data.stepDescriptionSize || 14),
                                '--big-size': appendPx(data.stepDescriptionSizeBig || data.stepDescriptionSize || 14)
                            },
                            'Step 2 description...'
                        )}
                    </div>

                    {/* Step 3 with Animation */}
                    <div 
                        key={`step3-${animationKey}`}
                        className={data.enableAnimations && isEditMode ? `methodology-animated ${data.animationType || 'fadeInUp'}` : ''}
                        style={{
                            flex: layoutConfig.stepsDirection === 'column' ? 'none' : '1',
                            textAlign: layoutConfig.stepsAlignment === 'center' || isMobileLayout ? 'center' : 'left',
                            width: layoutConfig.stepsDirection === 'column' ? '100%' : 'auto',
                            marginBottom: '0',
                            maxWidth: isMobileLayout ? '300px' : '600px',
                            margin: isMobileLayout ? '0 auto' : 
                                   layoutConfig.stepsDirection === 'column' ? '0' : '0',
                            animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                            animationDelay: data.enableAnimations && isEditMode ? `${(data.animationDelay || 200) * 3}ms` : '0s'
                        }}
                    >
                        {data.step3Number && renderEditableText(
                            'step3Number',
                            data.step3Number,
                            'div',
                            {
                                ...((!data.stepNumberEffect || data.stepNumberEffect === 'none') && {
                                    color: data.stepNumberColor || data.mainTitleColor || data.titleColor || '#000000' // ✅ IMPROVED: Mehrere Fallbacks
                                }),
                                fontSize: appendPx(data.stepNumberSize || 72),
                                fontWeight: data.stepNumberWeight || '300',
                                fontFamily: data.stepNumberFont || fonts.globalBigFont,
                                fontStyle: data.stepNumberStyle || 'italic',
                                marginBottom: '16px',
                                lineHeight: '1',
                                ...getTextEffectStyles(data.stepNumberEffect),
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepNumberSizeMobile || data.stepNumberSize || 72),
                                '--normal-size': appendPx(data.stepNumberSize || 72),
                                '--big-size': appendPx(data.stepNumberSizeBig || data.stepNumberSize || 72)
                            },
                            'Step 3 number...'
                        )}
                        
                        {data.step3Title && renderEditableText(
                            'step3Title',
                            data.step3Title,
                            'h3',
                            {
                                ...((!data.stepTitleEffect || data.stepTitleEffect === 'none') && {
                                    color: data.stepTitleColor || data.mainTitleColor || data.titleColor || '#000000' // ✅ IMPROVED: Mehrere Fallbacks
                                }),
                                fontSize: appendPx(data.stepTitleSize || 24),
                                fontWeight: data.stepTitleWeight || '400',
                                fontFamily: data.stepTitleFont || fonts.globalBigFont,
                                fontStyle: data.stepTitleStyle || 'italic',
                                marginBottom: '12px',
                                lineHeight: '1.3',
                                ...getTextEffectStyles(data.stepTitleEffect),
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepTitleSizeMobile || data.stepTitleSize || 24),
                                '--normal-size': appendPx(data.stepTitleSize || 24),
                                '--big-size': appendPx(data.stepTitleSizeBig || data.stepTitleSize || 24)
                            },
                            'Step 3 title...'
                        )}
                        
                        {data.step3Description && renderEditableText(
                            'step3Description',
                            data.step3Description,
                            'p',
                            {
                                color: data.stepDescriptionColor || data.mainDescriptionColor || data.additionalTextColor || '#666666', // ✅ IMPROVED: Mehrere Space-DB Fallbacks
                                fontSize: appendPx(data.stepDescriptionSize || 14),
                                fontWeight: data.stepDescriptionWeight || '300',
                                fontFamily: data.stepDescriptionFont || fonts.globalSmallFont,
                                lineHeight: '1.5',
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.stepDescriptionSizeMobile || data.stepDescriptionSize || 14),
                                '--normal-size': appendPx(data.stepDescriptionSize || 14),
                                '--big-size': appendPx(data.stepDescriptionSizeBig || data.stepDescriptionSize || 14)
                            },
                            'Step 3 description...'
                        )}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}

export default MethodologyWidget;
